import React from 'react';
import CollaborationMessageTranslations from './CollaborationMessageTranslations';
import type {
  ActionProposalDetails,
  ActionFieldUpdateDetail,
  CollaborationActionEditableFieldKey,
  CollaborationFindingEditableFieldKey,
  CollaborationSourceUpdateFieldResolutionMode,
  CollaborationSourceUpdateMode,
  CollaborationRuntimeAccess,
  CollaborationThreadEvent,
  CollaborationThreadSnapshot,
  CollaborationThreadSyncPackage,
  CollaborationThreadWorkItem,
  FindingFieldUpdateDetail,
  RecipientSyncPackageSubmissionType,
  RecipientSyncPackageFormState
} from './collaborationThreadShared';
import { extractSourceUpdateMode } from './collaborationThreadShared';

interface CollaborationRecipientThreadPaneProps {
  threadSnapshot: CollaborationThreadSnapshot;
  selectedInboxRuntimeAccess: CollaborationRuntimeAccess | null;
  partnerNameById: Map<string, string>;
  tenantUserEmail: string | null | undefined;
  recipientThreadMessageBody: string;
  postingRecipientThreadMessage: boolean;
  /** Tracker #57: message currently being translated ('' when idle). */
  translatingMessageId?: string;
  onTranslateMessage?: (messageId: string, targetLanguage: string, force?: boolean) => void;
  recipientSyncPackageForm: RecipientSyncPackageFormState;
  selectedRecipientWorkItem: CollaborationThreadWorkItem | null | undefined;
  selectedRecipientDraftSyncPackage: CollaborationThreadSyncPackage | null;
  selectedRecipientPendingSourceUpdate: CollaborationThreadSyncPackage | null;
  selectedRecipientSourceUpdateReviewFields: Array<
    {
      fieldKey: CollaborationFindingEditableFieldKey | CollaborationActionEditableFieldKey;
      fieldLabel: string;
      previousValue: string;
      sourceValue: string;
      draftValue: string;
      hasCollaboratorDraft: boolean;
      hasConflict: boolean;
      resolutionMode?: CollaborationSourceUpdateFieldResolutionMode;
    }
  >;
  selectedRecipientUnresolvedSourceUpdateFieldLabels: string[];
  submittingRecipientSyncPackage: boolean;
  savingRecipientSyncPackageDraft: boolean;
  withdrawingRecipientSyncPackageId: string;
  acknowledgingRecipientSourceUpdateId: string;
  withdrawingRecipientReturnedActionProposalId: string;
  downloadingRecipientArtifactId?: string;
  relativeTime: (value: string) => string;
  formatBytes: (value: number) => string;
  parseJsonSafe: (value: string) => Record<string, unknown>;
  extractActionProposalDetails: (payload: Record<string, unknown>) => ActionProposalDetails;
  extractFindingFieldUpdates: (payload: Record<string, unknown>) => FindingFieldUpdateDetail[];
  extractActionFieldUpdates: (payload: Record<string, unknown>) => ActionFieldUpdateDetail[];
  actionFieldUpdateLabel: (fieldKey: CollaborationActionEditableFieldKey) => string;
  findingFieldUpdateLabel: (fieldKey: CollaborationFindingEditableFieldKey) => string;
  describeWorkItemState: (workItem: CollaborationThreadWorkItem) => string | null;
  describeThreadEvent: (event: CollaborationThreadEvent) => string;
  canWithdrawActiveSyncPackage: (
    syncPackage: Pick<CollaborationThreadSyncPackage, 'status' | 'createdBy'>,
    actorEmail?: string | null
  ) => boolean;
  onCloseThread: () => void;
  onRecipientThreadMessageBodyChange: (value: string) => void;
  onPostRecipientThreadMessage: () => void;
  onRecipientWorkItemChange: (workItemId: string) => void;
  onResolveRecipientSourceUpdateField: (
    syncPackageId: string,
    fieldKey: CollaborationFindingEditableFieldKey | CollaborationActionEditableFieldKey,
    mode: CollaborationSourceUpdateFieldResolutionMode
  ) => void;
  onWithdrawRecipientSyncPackage: (syncPackageId: string) => void;
  onAcknowledgeRecipientSourceUpdate: (syncPackageId: string) => void;
  onWithdrawRecipientReturnedActionProposal: (returnedActionProposalId: string) => void;
  onDownloadRecipientDeliveredEvidence?: (artifactId: string, fileName: string) => void;
  onRecipientSyncPackageFormChange: (value: RecipientSyncPackageFormState) => void;
  onSaveRecipientSyncPackageDraft: () => void;
  onSubmitRecipientSyncPackage: () => void;
}

const CollaborationRecipientThreadPane = ({
  threadSnapshot,
  selectedInboxRuntimeAccess,
  partnerNameById,
  tenantUserEmail,
  recipientThreadMessageBody,
  postingRecipientThreadMessage,
  translatingMessageId = '',
  onTranslateMessage,
  recipientSyncPackageForm,
  selectedRecipientWorkItem,
  selectedRecipientDraftSyncPackage,
  selectedRecipientPendingSourceUpdate,
  selectedRecipientSourceUpdateReviewFields,
  selectedRecipientUnresolvedSourceUpdateFieldLabels,
  submittingRecipientSyncPackage,
  savingRecipientSyncPackageDraft,
  withdrawingRecipientSyncPackageId,
  acknowledgingRecipientSourceUpdateId,
  withdrawingRecipientReturnedActionProposalId,
  downloadingRecipientArtifactId,
  relativeTime,
  formatBytes,
  parseJsonSafe,
  extractActionProposalDetails,
  extractFindingFieldUpdates,
  extractActionFieldUpdates,
  actionFieldUpdateLabel,
  findingFieldUpdateLabel,
  describeWorkItemState,
  describeThreadEvent,
  canWithdrawActiveSyncPackage,
  onCloseThread,
  onRecipientThreadMessageBodyChange,
  onPostRecipientThreadMessage,
  onRecipientWorkItemChange,
  onResolveRecipientSourceUpdateField,
  onWithdrawRecipientSyncPackage,
  onAcknowledgeRecipientSourceUpdate,
  onWithdrawRecipientReturnedActionProposal,
  onDownloadRecipientDeliveredEvidence,
  onRecipientSyncPackageFormChange,
  onSaveRecipientSyncPackageDraft,
  onSubmitRecipientSyncPackage
}: CollaborationRecipientThreadPaneProps): JSX.Element => {
  const selectedPendingSourceUpdateMode: CollaborationSourceUpdateMode | null = selectedRecipientPendingSourceUpdate?.proposalJson
    ? extractSourceUpdateMode(parseJsonSafe(selectedRecipientPendingSourceUpdate.proposalJson))
    : null;
  const selectedActionFieldContext =
    selectedRecipientWorkItem?.objectType === 'action'
      ? threadSnapshot.fieldUpdateContext?.actionFieldContextsByObjectId[selectedRecipientWorkItem.objectId]
      : null;

  return (
    <section className="card collaboration-thread-workspace">
    <div className="collaboration-thread-header">
      <div>
        <h2 id="received-thread-heading" tabIndex={-1}>{threadSnapshot.thread.titleSnapshot}</h2>
        <div className="muted">
          Received supplier quality thread · {threadSnapshot.thread.rootObjectType} {threadSnapshot.thread.rootObjectId}
        </div>
        <div className="muted">
          Dialogue for supplier quality items imported into your installation. Started {relativeTime(threadSnapshot.thread.createdAtUtc)}
        </div>
        {selectedInboxRuntimeAccess && (
          <div className="muted">
            {selectedInboxRuntimeAccess.message}
            {selectedInboxRuntimeAccess.boundParentTenantId
              ? ` | bound parent ${selectedInboxRuntimeAccess.boundParentTenantId}`
              : ''}
          </div>
        )}
      </div>
      <div className="collaboration-work-row__actions">
        <a className="btn" href="#received-thread-response">Respond to this work</a>
        <button className="btn secondary" type="button" onClick={onCloseThread}>
          ← Back to Received work
        </button>
      </div>
    </div>

    <div style={{ display: 'grid', gap: '10px', marginTop: '12px' }}>
      <div>
        <strong>Participants</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.participants.length === 0 && <span className="badge-subtle">No participants recorded yet.</span>}
          {threadSnapshot.participants.map((participant) => (
            <div key={participant.participantId} className="list-row" style={{ cursor: 'default' }}>
              <div>
                <strong>{participant.displayNameSnapshot || participant.emailNormalized || participant.participantType}</strong>
                <div className="muted">
                  {participant.participantRole} | {participant.participantType} | {participant.isActive ? 'active' : 'inactive'}
                </div>
                {participant.partnerId && (
                  <div className="muted">Supplier: {partnerNameById.get(participant.partnerId) || participant.partnerId}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <strong>Dialogue</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.messages.length === 0 && <span className="badge-subtle">No dialogue messages recorded yet.</span>}
          {threadSnapshot.messages.map((message) => (
            <div key={message.messageId} className="list-row" style={{ cursor: 'default' }}>
              <div>
                <strong>{message.originDisplayName || message.originEmail || message.originType}</strong>
                <div className="muted">
                  {message.sourceChannel} | {relativeTime(message.createdAtUtc)}
                </div>
                <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>{message.body}</div>
                {onTranslateMessage && (
                  <CollaborationMessageTranslations
                    message={message}
                    translatingMessageId={translatingMessageId}
                    relativeTime={relativeTime}
                    onTranslate={onTranslateMessage}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <textarea
          className="input"
          style={{ marginTop: '10px', minHeight: '88px' }}
          placeholder="Add a thread message"
          value={recipientThreadMessageBody}
          onChange={(event) => onRecipientThreadMessageBodyChange(event.target.value)}
        />
        <div style={{ marginTop: '8px' }}>
          <button className="btn secondary" type="button" onClick={onPostRecipientThreadMessage} disabled={postingRecipientThreadMessage}>
            {postingRecipientThreadMessage ? 'Posting…' : 'Post message'}
          </button>
        </div>
      </div>

      <div>
        <strong>Response history</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.syncPackages.length === 0 && <span className="badge-subtle">No sync packages recorded yet.</span>}
          {threadSnapshot.syncPackages.map((syncPackage) => {
            const workItem = threadSnapshot.workItems.find((entry) => entry.workItemId === syncPackage.workItemId);
            const proposal = syncPackage.proposalJson ? parseJsonSafe(syncPackage.proposalJson) : {};
            const findingFieldUpdates = extractFindingFieldUpdates(proposal);
            const actionFieldUpdates = extractActionFieldUpdates(proposal);
            return (
              <div key={syncPackage.syncPackageId} className="list-row" style={{ cursor: 'default' }}>
                <div>
                  <strong>{syncPackage.packageType}</strong>
                  <div className="muted">
                    {syncPackage.status} | {workItem?.titleSnapshot || `${workItem?.objectType || 'item'} ${workItem?.objectId || syncPackage.workItemId}`} | Submitted{' '}
                    {relativeTime(syncPackage.submittedAtUtc || syncPackage.createdAtUtc)}
                  </div>
                  {syncPackage.reviewedAtUtc && (
                    <div className="muted">
                      {syncPackage.status === 'withdrawn' ? 'Withdrawn' : 'Reviewed'} {relativeTime(syncPackage.reviewedAtUtc)}
                      {syncPackage.reviewedBy ? ` by ${syncPackage.reviewedBy}` : ''}
                    </div>
                  )}
                  {syncPackage.reviewNote && (
                    <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>{syncPackage.reviewNote}</div>
                  )}
                  {syncPackage.status === 'stale_conflict' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Origin changed after submission. Submit a fresh package against the latest version.
                    </div>
                  )}
                  {syncPackage.status === 'withdrawn' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      This package was withdrawn before owner review and no longer blocks a fresh resubmission.
                    </div>
                  )}
                  {findingFieldUpdates.length > 0 && (
                    <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                      {findingFieldUpdates.map((fieldUpdate) => (
                        <div key={`${syncPackage.syncPackageId}-${fieldUpdate.fieldKey}`} className="muted">
                          {findingFieldUpdateLabel(fieldUpdate.fieldKey)}: {fieldUpdate.proposedValue || '(cleared)'}
                        </div>
                      ))}
                    </div>
                  )}
                  {actionFieldUpdates.length > 0 && (
                    <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                      {actionFieldUpdates.map((fieldUpdate) => (
                        <div key={`${syncPackage.syncPackageId}-${fieldUpdate.fieldKey}`} className="muted">
                          {actionFieldUpdateLabel(fieldUpdate.fieldKey)}: {fieldUpdate.proposedValue || '(cleared)'}
                        </div>
                      ))}
                    </div>
                  )}
                  {syncPackage.fieldImportStatus === 'imported' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Source reviewed this field update{syncPackage.fieldImportedAtUtc ? ` ${relativeTime(syncPackage.fieldImportedAtUtc)}` : ''}.
                    </div>
                  )}
                  {syncPackage.packageType === 'source_update' && syncPackage.sourceUpdateAckStatus === 'pending_ack' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      {extractSourceUpdateMode(proposal) === 'published_new_baseline'
                        ? 'Source published a new Finding baseline. Rebase your draft onto the changed values and acknowledge before submitting more supplier quality work.'
                        : 'Source updated governed source-owned Finding fields. Review the changed values and acknowledge before submitting more supplier quality work.'}
                    </div>
                  )}
                  {syncPackage.packageType === 'source_update' && syncPackage.sourceUpdateAckStatus === 'acknowledged' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Source update acknowledged
                      {syncPackage.sourceUpdateAcknowledgedAtUtc ? ` ${relativeTime(syncPackage.sourceUpdateAcknowledgedAtUtc)}` : ''}
                      {syncPackage.sourceUpdateAcknowledgedBy ? ` by ${syncPackage.sourceUpdateAcknowledgedBy}` : ''}.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {syncPackage.packageType === 'source_update' && syncPackage.sourceUpdateAckStatus === 'pending_ack' && (
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onAcknowledgeRecipientSourceUpdate(syncPackage.syncPackageId)}
                      disabled={
                        acknowledgingRecipientSourceUpdateId === syncPackage.syncPackageId ||
                        (selectedPendingSourceUpdateMode === 'published_new_baseline' &&
                          selectedRecipientPendingSourceUpdate?.syncPackageId === syncPackage.syncPackageId &&
                          selectedRecipientUnresolvedSourceUpdateFieldLabels.length > 0)
                      }
                    >
                      {acknowledgingRecipientSourceUpdateId === syncPackage.syncPackageId ? 'Acknowledging…' : 'Acknowledge source update'}
                    </button>
                  )}
                  {canWithdrawActiveSyncPackage(syncPackage, tenantUserEmail) && (
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onWithdrawRecipientSyncPackage(syncPackage.syncPackageId)}
                      disabled={withdrawingRecipientSyncPackageId === syncPackage.syncPackageId}
                    >
                      {withdrawingRecipientSyncPackageId === syncPackage.syncPackageId ? 'Withdrawing…' : 'Withdraw'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <strong>Activity</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.events.length === 0 && <span className="badge-subtle">No lifecycle activity recorded yet.</span>}
          {threadSnapshot.events.map((event) => (
            <div key={event.eventId} className="list-row" style={{ cursor: 'default' }}>
              <div>
                <strong>{describeThreadEvent(event)}</strong>
                <div className="muted">
                  {relativeTime(event.createdAtUtc)}
                  {event.actor ? ` | ${event.actor}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <strong>Evidence</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.artifacts.length === 0 && <span className="badge-subtle">No returned artifacts recorded yet.</span>}
          {threadSnapshot.artifacts.map((artifact) => {
            const isDeliveredEvidence = artifact.artifactKind === 'source_evidence';
            const canDownloadDeliveredEvidence =
              isDeliveredEvidence &&
              artifact.quarantineStatus === 'released' &&
              !artifact.binaryPurgedAtUtc &&
              Boolean(onDownloadRecipientDeliveredEvidence);
            return (
              <div key={artifact.artifactId} className="list-row" style={{ cursor: 'default' }}>
                <div>
                  <strong>{artifact.fileName}</strong>
                  <div className="muted">
                    {isDeliveredEvidence ? 'Evidence from customer' : artifact.artifactKind} | {formatBytes(artifact.sizeBytes)} | {artifact.quarantineStatus}
                    {artifact.attestationVerdict ? ` | scan ${artifact.attestationVerdict}` : ''}
                    {artifact.binaryPurgedAtUtc ? ` | binary purged ${relativeTime(artifact.binaryPurgedAtUtc)}` : ''}
                  </div>
                  {isDeliveredEvidence && artifact.quarantineStatus === 'quarantined' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      This customer evidence file is quarantined pending release and cannot be downloaded yet.
                    </div>
                  )}
                  {artifact.binaryPurgeReason && (
                    <div className="muted" style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                      Purge reason: {artifact.binaryPurgeReason}
                    </div>
                  )}
                </div>
                {canDownloadDeliveredEvidence && (
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => onDownloadRecipientDeliveredEvidence?.(artifact.artifactId, artifact.fileName)}
                    disabled={downloadingRecipientArtifactId === artifact.artifactId}
                  >
                    {downloadingRecipientArtifactId === artifact.artifactId ? 'Downloading…' : 'Download'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <strong>Returned Action Proposals</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.returnedActionProposals.length === 0 && (
            <span className="badge-subtle">No returned Action proposals recorded yet.</span>
          )}
          {threadSnapshot.returnedActionProposals.map((proposal) => {
            const proposalPayload = parseJsonSafe(proposal.proposalJson);
            const actionProposal = extractActionProposalDetails(proposalPayload);
            const canWithdraw =
              proposal.proposalStatus === 'returned_pending_adoption' &&
              String(proposal.submittedBy || '').trim().toLowerCase() === String(tenantUserEmail || '').trim().toLowerCase();
            return (
              <div key={proposal.returnedActionProposalId} className="list-row" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <strong>{proposal.titleSnapshot || actionProposal.title || 'Untitled returned Action'}</strong>
                  <div className="muted">
                    {proposal.proposalStatus} | Submitted {relativeTime(proposal.createdAtUtc)}
                    {proposal.submittedBy ? ` by ${proposal.submittedBy}` : ''}
                  </div>
                  {(proposal.summarySnapshot || actionProposal.description || actionProposal.summary) && (
                    <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                      {proposal.summarySnapshot || actionProposal.description || actionProposal.summary}
                    </div>
                  )}
                  {proposal.adoptedOriginObjectId && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Adopted origin action id: {proposal.adoptedOriginObjectId}
                    </div>
                  )}
                  {proposal.importedAtUtc && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Imported into origin {relativeTime(proposal.importedAtUtc)}
                      {proposal.importedBy ? ` by ${proposal.importedBy}` : ''}
                    </div>
                  )}
                </div>
                {canWithdraw && (
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => onWithdrawRecipientReturnedActionProposal(proposal.returnedActionProposalId)}
                    disabled={withdrawingRecipientReturnedActionProposalId === proposal.returnedActionProposalId}
                  >
                    {withdrawingRecipientReturnedActionProposalId === proposal.returnedActionProposalId ? 'Withdrawing…' : 'Withdraw'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div id="received-thread-response">
        <strong>Prepare a response</strong>
        <div className="grid columns-2" style={{ marginTop: '10px' }}>
          <select
            className="input"
            value={recipientSyncPackageForm.workItemId}
            onChange={(event) => onRecipientWorkItemChange(event.target.value)}
          >
            <option value="">Select work item</option>
            {threadSnapshot.workItems.map((workItem) => (
              <option key={workItem.workItemId} value={workItem.workItemId}>
                {`${workItem.titleSnapshot || `${workItem.objectType} ${workItem.objectId}`} (${workItem.workingState})`}
              </option>
            ))}
          </select>
          <select
            className="input"
            value={recipientSyncPackageForm.packageType}
            onChange={(event) =>
              onRecipientSyncPackageFormChange({
                ...recipientSyncPackageForm,
                packageType: event.target.value as RecipientSyncPackageSubmissionType
              })
            }
          >
            <option value="field_update">Field update</option>
            <option value="action_proposal">Action proposal</option>
            <option value="request_close">Request close</option>
          </select>
        </div>
        {selectedRecipientWorkItem && describeWorkItemState(selectedRecipientWorkItem) && (
          <div className="muted" style={{ marginTop: '8px' }}>
            {describeWorkItemState(selectedRecipientWorkItem)}
          </div>
        )}
        {selectedRecipientDraftSyncPackage && (
          <div className="muted" style={{ marginTop: '8px' }}>
            Saved field-update draft from {relativeTime(selectedRecipientDraftSyncPackage.updatedAtUtc || selectedRecipientDraftSyncPackage.createdAtUtc)}.
            Submitting will promote that draft into owner review.
          </div>
        )}
        {selectedRecipientPendingSourceUpdate && (
          <div className="muted" style={{ marginTop: '8px' }}>
            {selectedPendingSourceUpdateMode === 'published_new_baseline'
              ? 'This work item has a pending source baseline update. Review the changed values above, rebase your field-update draft below if needed, then acknowledge the source update.'
              : 'This work item has a pending source-owned update from the origin. Review it and acknowledge before further submissions.'}
          </div>
        )}
        {selectedRecipientPendingSourceUpdate && selectedPendingSourceUpdateMode === 'published_new_baseline' && (
          <div style={{ display: 'grid', gap: '8px', marginTop: '10px' }}>
            <div className="muted">
              Review each source-changed governed field before acknowledging the new baseline.
              {selectedRecipientUnresolvedSourceUpdateFieldLabels.length > 0
                ? ` Still unresolved: ${selectedRecipientUnresolvedSourceUpdateFieldLabels.join(', ')}.`
                : ' All changed fields have been reviewed.'}
            </div>
            {selectedRecipientSourceUpdateReviewFields.map((entry) => (
              <div key={`${selectedRecipientPendingSourceUpdate.syncPackageId}-${entry.fieldKey}`} className="list-row" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <strong>{entry.fieldLabel}</strong>
                  <div className="muted" style={{ marginTop: '4px' }}>
                    {entry.hasConflict
                      ? 'Conflict: both source and supplier changed this field.'
                      : entry.hasCollaboratorDraft
                        ? 'Supplier draft exists for this field.'
                        : 'Source-only update on this field.'}
                  </div>
                  <div className="muted" style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                    Previous baseline: {entry.previousValue || '(empty)'}
                  </div>
                  <div className="muted" style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                    New source value: {entry.sourceValue || '(empty)'}
                  </div>
                  <div className="muted" style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                    Your current draft: {entry.draftValue || '(empty)'}
                  </div>
                  {entry.resolutionMode && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Resolution selected: {entry.resolutionMode.replace(/_/g, ' ')}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() =>
                      onResolveRecipientSourceUpdateField(
                        selectedRecipientPendingSourceUpdate.syncPackageId,
                        entry.fieldKey,
                        'use_source'
                      )
                    }
                  >
                    Use source
                  </button>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() =>
                      onResolveRecipientSourceUpdateField(
                        selectedRecipientPendingSourceUpdate.syncPackageId,
                        entry.fieldKey,
                        'keep_mine'
                      )
                    }
                  >
                    Keep mine
                  </button>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() =>
                      onResolveRecipientSourceUpdateField(
                        selectedRecipientPendingSourceUpdate.syncPackageId,
                        entry.fieldKey,
                        'manual_merge'
                      )
                    }
                  >
                    Merge manually
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <textarea
          className="input"
          style={{ marginTop: '10px', minHeight: '88px' }}
          placeholder="Describe the proposed update"
          value={recipientSyncPackageForm.summary}
          onChange={(event) =>
            onRecipientSyncPackageFormChange({ ...recipientSyncPackageForm, summary: event.target.value })
          }
        />
        {recipientSyncPackageForm.packageType === 'field_update' && selectedRecipientWorkItem?.objectType !== 'action' && (
          <div className="grid columns-2" style={{ marginTop: '10px' }}>
            {(threadSnapshot.fieldUpdateContext?.editableFindingFieldKeys || []).map((fieldKey) => (
              <div key={fieldKey}>
                <label style={{ display: 'block', marginBottom: '6px' }}>{findingFieldUpdateLabel(fieldKey)}</label>
                <textarea
                  className="input"
                  rows={fieldKey === 'reference' ? 2 : 4}
                  placeholder={
                    threadSnapshot.fieldUpdateContext?.findingFieldSnapshot[fieldKey]
                      ? `Current: ${threadSnapshot.fieldUpdateContext.findingFieldSnapshot[fieldKey]}`
                      : `Propose an updated ${findingFieldUpdateLabel(fieldKey).toLowerCase()}`
                  }
                  value={recipientSyncPackageForm.findingFieldUpdates[fieldKey]}
                  onChange={(event) =>
                    onRecipientSyncPackageFormChange({
                      ...recipientSyncPackageForm,
                      findingFieldUpdates: {
                        ...recipientSyncPackageForm.findingFieldUpdates,
                        [fieldKey]: event.target.value
                      }
                    })
                  }
                />
              </div>
            ))}
          </div>
        )}
        {recipientSyncPackageForm.packageType === 'field_update' && selectedRecipientWorkItem?.objectType === 'action' && (
          <div className="grid columns-2" style={{ marginTop: '10px' }}>
            {(selectedActionFieldContext?.editableActionFieldKeys || []).map((fieldKey) => (
              <div key={fieldKey}>
                <label style={{ display: 'block', marginBottom: '6px' }}>{actionFieldUpdateLabel(fieldKey)}</label>
                <input
                  className="input"
                  type={fieldKey === 'targetDate' ? 'date' : 'text'}
                  placeholder={
                    selectedActionFieldContext?.actionFieldSnapshot[fieldKey]
                      ? `Current: ${selectedActionFieldContext.actionFieldSnapshot[fieldKey]}`
                      : `Propose an updated ${actionFieldUpdateLabel(fieldKey).toLowerCase()}`
                  }
                  value={recipientSyncPackageForm.actionFieldUpdates[fieldKey]}
                  onChange={(event) =>
                    onRecipientSyncPackageFormChange({
                      ...recipientSyncPackageForm,
                      actionFieldUpdates: {
                        ...recipientSyncPackageForm.actionFieldUpdates,
                        [fieldKey]: event.target.value
                      }
                    })
                  }
                />
              </div>
            ))}
          </div>
        )}
        {recipientSyncPackageForm.packageType === 'action_proposal' && (
          <div className="grid columns-2" style={{ marginTop: '10px' }}>
            <input
              className="input"
              placeholder="Action title"
              value={recipientSyncPackageForm.actionProposalTitle}
              onChange={(event) =>
                onRecipientSyncPackageFormChange({
                  ...recipientSyncPackageForm,
                  actionProposalTitle: event.target.value
                })
              }
            />
            <input
              className="input"
              placeholder="Suggested owner email"
              value={recipientSyncPackageForm.actionProposalOwnerEmail}
              onChange={(event) =>
                onRecipientSyncPackageFormChange({
                  ...recipientSyncPackageForm,
                  actionProposalOwnerEmail: event.target.value
                })
              }
            />
            <input
              className="input"
              placeholder="Suggested target date"
              type="date"
              value={recipientSyncPackageForm.actionProposalTargetDate}
              onChange={(event) =>
                onRecipientSyncPackageFormChange({
                  ...recipientSyncPackageForm,
                  actionProposalTargetDate: event.target.value
                })
              }
            />
            <select
              className="input"
              value={recipientSyncPackageForm.actionProposalStatus}
              onChange={(event) =>
                onRecipientSyncPackageFormChange({
                  ...recipientSyncPackageForm,
                  actionProposalStatus: event.target.value
                })
              }
            >
              <option value="">Use origin default</option>
              <option value="Open">Open</option>
              <option value="In progress">In progress</option>
              <option value="Closed">Closed</option>
            </select>
            <input
              className="input"
              placeholder="Verification person email"
              value={recipientSyncPackageForm.actionProposalVerificationPersonEmail}
              onChange={(event) =>
                onRecipientSyncPackageFormChange({
                  ...recipientSyncPackageForm,
                  actionProposalVerificationPersonEmail: event.target.value
                })
              }
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={recipientSyncPackageForm.actionProposalMitigatesRisk}
                onChange={(event) =>
                  onRecipientSyncPackageFormChange({
                    ...recipientSyncPackageForm,
                    actionProposalMitigatesRisk: event.target.checked
                  })
                }
              />
              Mitigates finding risk
            </label>
            <textarea
              className="input"
              style={{ gridColumn: '1 / -1', minHeight: '72px' }}
              placeholder="Action details"
              value={recipientSyncPackageForm.actionProposalDescription}
              onChange={(event) =>
                onRecipientSyncPackageFormChange({
                  ...recipientSyncPackageForm,
                  actionProposalDescription: event.target.value
                })
              }
            />
            <textarea
              className="input"
              style={{ gridColumn: '1 / -1', minHeight: '72px' }}
              placeholder="Verification notes"
              value={recipientSyncPackageForm.actionProposalVerificationNotes}
              onChange={(event) =>
                onRecipientSyncPackageFormChange({
                  ...recipientSyncPackageForm,
                  actionProposalVerificationNotes: event.target.value
                })
              }
            />
          </div>
        )}
        {threadSnapshot.textOnly ? (
          // Tracker #117: the customer started this collaboration as text only,
          // so no files can be returned on it. The server refuses them too.
          <p className="muted" style={{ marginTop: '10px' }}>
            This collaboration is text only. Reply in text; files cannot be attached.
          </p>
        ) : (
          <div className="grid columns-2" style={{ marginTop: '10px' }}>
            <input
              className="input"
              type="file"
              multiple
              onChange={(event) =>
                onRecipientSyncPackageFormChange({
                  ...recipientSyncPackageForm,
                  files: Array.from(event.target.files || [])
                })
              }
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={recipientSyncPackageForm.filesRequired}
                onChange={(event) =>
                  onRecipientSyncPackageFormChange({
                    ...recipientSyncPackageForm,
                    filesRequired: event.target.checked
                  })
                }
              />
              Files are required before owner review
            </label>
          </div>
        )}
        <div style={{ marginTop: '8px' }}>
          {recipientSyncPackageForm.packageType === 'field_update' && (
            <button
              className="btn secondary"
              type="button"
              onClick={onSaveRecipientSyncPackageDraft}
              disabled={savingRecipientSyncPackageDraft || submittingRecipientSyncPackage}
              style={{ marginRight: '8px' }}
            >
              {savingRecipientSyncPackageDraft ? 'Saving draft…' : 'Save response draft'}
            </button>
          )}
          <button className="btn secondary" type="button" onClick={onSubmitRecipientSyncPackage} disabled={submittingRecipientSyncPackage}>
            {submittingRecipientSyncPackage ? 'Submitting…' : 'Submit response for customer review'}
          </button>
        </div>
      </div>
    </div>
    </section>
  );
};

export default CollaborationRecipientThreadPane;
